import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { gridSizeMdLg12, gridSizeMdLg6, ROUTER_PATHS, useCheckPermissionsArray, useLoaders } from '@/shared';
import {
  useFetchAllCountries,
  useFetchCitiesEnabled,
  useFetchProvinces,
} from '@/shared/actions/erp';
import {
  CreateZoneParams,
  useCreateZone,
  useUpdateZone,
} from '@/shared/actions/erp';
import {
  CustomAutocomplete,
  CustomTextField,
  SampleCheckbox,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import { AlertSeverityEnum } from '@/shared/interfaces';
import {
  City,
  Country,
  Province,
} from '@/shared/interfaces/erp';
import { newZoneSchema } from '@/shared/utils';
import { useUiStore } from '@/store/ui';
import { RELATED_PERMISSIONS_ZONE } from '@/erp/sytem-sttings/zones/shared/constants';
import { Zone } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import CustomMapZoneCreate from '../CustomMapZoneCreate/CustomMapZoneCreate';

export type SaveZoneProps = {
  titlePage: string;
  editZone?: Zone;
};

export type SaveFormData = CreateZoneParams & {};

const SaveZone: React.FC<SaveZoneProps> = ({ titlePage, editZone }) => {
  useCheckPermissionsArray(RELATED_PERMISSIONS_ZONE);
  const returnUrlZonesPage =
    ROUTER_PATHS.mantenimientoOperaciones.zonasNav;
  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);
  const [polygon, setPolygon] = useState<any[] | null>(null);
  const [centerMap, setCenterMap] = useState<[number, number]>([-0.3208730168341767, -78.47201823285198])

  ///* global state ---------------------
  const setAlert = useUiStore(s => s.setAlert);

  ///* mutations ---------------------
  const createZoneMutation = useCreateZone({
    navigate,
    returnUrl: returnUrlZonesPage,
  });
  const updateZoneMutation = useUpdateZone({
    navigate,
    returnUrl: returnUrlZonesPage,
  });

  ///* form ---------------------
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newZoneSchema) as any,
    defaultValues: {
      estado: true,
      cobertura: true,
    },
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = form;

  const watchedCountry = form.watch('pais');
  const watchedProvince = form.watch('provincia');

  ///* fetch data ---------------------
  const {
    data: countriesPaging,
    isLoading: isLoadingCountries,
    isRefetching: isRefetchingCountries,
  } = useFetchAllCountries({
    page_size: 600,
  });
  const {
    data: provincesPaging,
    isLoading: isLoadingProvinces,
    isRefetching: isRefetchingProvinces,
  } = useFetchProvinces(
    {
      page_size: 600,
      pais: watchedCountry,
    },
    !!watchedCountry
  );
  const {
    data: citiesPaging,
    isLoading: isLoadingCities,
    isRefetching: isRefetchingCities,
  } = useFetchCitiesEnabled(
    {
      params: {
        page_size: 600,
        provincia: watchedProvince,
        pais: watchedCountry,
      },
      enabled: !!watchedProvince && !!watchedCountry
    }
  );


  ///* handlers ---------------------
  const onSave = () => {

    if (polygon == null) return setAlert(true, 'Asignar una zona', AlertSeverityEnum.error)

    if (!isValid) return;

    const formulario = {
      ...form.getValues(),
      coordenadas_array: polygon.map(item => ({
        lat: item.lat.toString(),
        lng: item.lng.toString(),
      }))
    }

    ///* edit
    if (editZone?.id_zona) {
      return updateZoneMutation.mutate({
        id: editZone.id_zona,
        data: formulario,
      });
    }

    ///* create
    return createZoneMutation.mutate(formulario);
  };

  ///* effects ---------------------
  useEffect(() => {
    if (!editZone?.id_zona) return;
    setShrink(true);
    reset(editZone as any);

    if (editZone.coordenadas_array) {
      const dataCoordenas = editZone.coordenadas_array!.map(item => ({
        lat: +item.lat,
        lng: +item.lng
      }))

      setCenterMap([dataCoordenas.at(0)?.lat!, dataCoordenas.at(0)?.lng!]);
      setPolygon(dataCoordenas);
    }

  }, []);

  // alerts ---
  // province
  useEffect(() => {
    if (isLoadingProvinces || isRefetchingProvinces) return;

    if (watchedCountry && !provincesPaging?.data?.data?.length) {
      setAlert(
        true,
        'No se encontraron provincias disponibles',
        AlertSeverityEnum.warning
      );
    }
  }, [
    watchedCountry,
    provincesPaging,
    isLoadingProvinces,
    isRefetchingProvinces,
  ]);
  // city
  useEffect(() => {
    if (isLoadingCities || isRefetchingCities) return;

    if (watchedProvince && !citiesPaging?.data?.data?.length) {
      setAlert(
        true,
        'No se encontraron ciudades disponibles',
        AlertSeverityEnum.warning
      );
    }
  }, [watchedProvince, citiesPaging, isLoadingCities, isRefetchingCities]);

  useLoaders(
    isLoadingProvinces ||
    isLoadingCountries ||
    isRefetchingProvinces ||
    isRefetchingCountries ||
    isLoadingCities ||
    isRefetchingCities
  );

  if (editZone && (isLoadingCountries || isLoadingProvinces || isLoadingCities))
    return null;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlZonesPage);
      }}
      onSave={handleSubmit(onSave, err => {
        console.log(err);
      })}
    >


      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message || ''}
        shrink={shrink}
      />
      <CustomTextField
        type='none'
        label="Número Referencia"
        name="numero_referencia"
        control={form.control}
        defaultValue={form.getValues().numero_referencia || ''}
        error={errors.numero_referencia}
        helperText={errors.numero_referencia?.message ?? ''}
        shrink={shrink}
        size={gridSizeMdLg6}
      />

      {/* ======== Autocomplete city ======== */}
      <CustomAutocomplete<Country>
        label="País"
        name="pais"
        // options
        options={countriesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().pais || ''}
        optionLabelForEdit={form.getValues().pais || ''}
        isLoadingData={isLoadingCountries}
        // validation
        control={control}
        error={errors.pais}
        helperText={errors.pais?.message}
        size={gridSizeMdLg6}
        onChangeValue={() => {
          form.setValue('provincia', '');
          form.setValue('ciudad', '');
        }}
      />
      <CustomAutocomplete<Province>
        label="Provincia"
        name="provincia"
        // options
        options={provincesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey='codigo'
        defaultValue={form.getValues().provincia || ''}
        optionLabelForEdit={form.getValues().provincia || ''}
        isLoadingData={isLoadingProvinces}
        // validation
        control={control}
        error={errors.provincia}
        helperText={errors.provincia?.message}
        size={gridSizeMdLg6}
        onChangeValue={() => {
          form.setValue('ciudad', '');
        }}
      />

      <CustomAutocomplete<City>
        label="Ciudad"
        name="ciudad"
        // options
        options={citiesPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey='codigo'
        defaultValue={form.getValues().ciudad || ''}
        optionLabelForEdit={form.getValues().ciudad}
        isLoadingData={isLoadingCities}
        // validation
        control={control}
        error={errors.ciudad}
        helperText={errors.ciudad?.message}
        size={gridSizeMdLg6}
      />

      <CustomMapZoneCreate
        center={centerMap}
        polygon={polygon}
        setPolygon={setPolygon}
        codigo_zona={editZone?.codigo}
      />

      <SampleCheckbox
        label="Tiene cobertura"
        name="cobertura"
        control={form.control}
        defaultValue={!!form.getValues().cobertura}
        size={gridSizeMdLg12}
      />
      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
        size={gridSizeMdLg6}
      />




    </SingleFormBoxScene>
  );
};

export default SaveZone;